import { resolveNewSupplierManager } from "./IngredientForm/Pages/Page2/supplierMutationPermission";
import { Ingredient } from "src/api/client-api/ingredients/useGetOneIngredient";
import {
    IngredientFormAssociatedCompany,
    IngredientFormIngredientCompany,
    IngredientFormType,
} from "./IngredientForm/IngredientForm.types";
import {
    IngredientWrite,
    IngredientWriteIngredientCompany,
    IngredientWriteIngredientSupplier,
} from "src/api/client-api/ingredients/useUpsertIngredient";
import { formatStringForBackend } from "src/utils/formatting";

export class IngredientService {
    private static convertArrayTagsToTags = (arrayTags: any[]): Record<string, string[]> => {
        const tagsMap: Record<string, string[]> = {};

        arrayTags.forEach((tag) => {
            const tagTypeName = tag.type;
            const tagTypeSlug = tagTypeName?.toLowerCase().replace(/[^a-zA-Z0-9]+/g, "-");
            const tagValue = tag.value;

            if (tagTypeSlug && tagValue) {
                if (!tagsMap[tagTypeSlug]) {
                    tagsMap[tagTypeSlug] = [];
                }
                if (!tagsMap[tagTypeSlug].includes(tagValue)) {
                    tagsMap[tagTypeSlug].push(tagValue);
                }
            }
        });

        return tagsMap;
    };

    static getIngredientToCreate = (existingIngredient?: Ingredient): IngredientFormType => {
        if (existingIngredient === undefined) {
            const newIngredient: IngredientFormType = {
                name: null,
                clientId: null,
                ingredientReferenceId: null,
                referenceSource: "sure",
                ingredientLine: null,
                organic: null,
                ownedBy: null,
                suppliers: [],
                shares: [],
                tags: {},
                jsonAttributes: {},
            };
            return newIngredient;
        }

        const ingSuppliers = existingIngredient.suppliers?.map((existingIngSup) => {
            const supplier = existingIngSup.supplier;
            const managedById = existingIngSup.managedBy?.id;
            const existingToCreate: IngredientFormIngredientCompany = {
                "@id": existingIngSup["@id"],
                supplier: {
                    "@id": supplier["@id"],
                    name: supplier.name,
                    clientId: supplier.clientId ?? null,
                },
                managedBy: managedById === undefined ? undefined : { id: managedById },
                ingredientReference: existingIngSup.ingredientReference ?? null,
                transportSelections: {},
                transportSelectionsStatus: "unloaded",
            };
            return existingToCreate;
        });

        const ingCompanies = existingIngredient.shares?.map((existingIngComp) => {
            const company = existingIngComp.organization;
            return {
                "@id": existingIngComp["@id"],
                id: existingIngComp.id,
                organization: {
                    "@id": company["@id"],
                    id: company.id,
                    name: company.name,
                },
            };
        });

        // Convert tags to form format
        let tags: Record<string, string[]> = {};
        if (existingIngredient.tags && Array.isArray(existingIngredient.tags)) {
            tags = IngredientService.convertArrayTagsToTags(existingIngredient.tags);
        } else if (
            existingIngredient.tags &&
            typeof existingIngredient.tags === "object" &&
            !Array.isArray(existingIngredient.tags)
        ) {
            // Already in Record<string, string[]> format
            tags = existingIngredient.tags;
        }

        const formattedIngredient: IngredientFormType = {
            id: existingIngredient.id,
            name: existingIngredient.name,
            clientId: existingIngredient.clientId ?? null,
            ingredientReferenceId: existingIngredient.ingredientReferenceId ?? null,
            referenceSource: existingIngredient.referenceSource ?? "sure",
            ingredientLine: existingIngredient.ingredientLine ?? null,
            organic: existingIngredient.organic ?? null,
            ownedBy: existingIngredient.ownedBy ?? null,
            canModify: existingIngredient.canModify,
            suppliers: ingSuppliers ?? [],
            shares: ingCompanies ?? [],
            tags: tags,
            jsonAttributes: existingIngredient.jsonAttributes ?? {},
        };
        return formattedIngredient;
    };

    private static makeIngredientSuppliers = (
        suppliers: IngredientFormIngredientCompany[],
        managedBy: IRI,
    ): IngredientWriteIngredientSupplier[] => {
        const newIngredientSuppliers = suppliers.map((ingSupplier) => {
            const ingSuppId = ingSupplier["@id"];
            // We round-trip the `ingredientReference` even on existing rows because the
            // user might assign / clear it without otherwise touching the supplier link.
            // `null` is sent on purpose to mean "clear the reference"; an undefined value
            // would be omitted from the JSON-LD payload and the backend would keep the
            // current relation. The IRI form is reserved for "leave entirely untouched".
            const referenceIri = ingSupplier.ingredientReference?.["@id"] ?? null;

            if (ingSuppId) {
                const existingIngSupp: IngredientWriteIngredientSupplier = {
                    "@id": ingSuppId,
                    ingredientReference: referenceIri,
                };
                return existingIngSupp;
            }

            const newIngSupplier: IngredientWriteIngredientSupplier = {
                supplier: ingSupplier.supplier["@id"],
                managedBy,
                ingredientReference: referenceIri,
            };
            return newIngSupplier;
        });

        return newIngredientSuppliers;
    };

    private static makeShares = (shares: IngredientFormAssociatedCompany[]): IngredientWriteIngredientCompany[] => {
        return shares.map((ingComp) => {
            const ingCompId = ingComp["@id"];
            if (ingCompId) {
                return ingCompId;
            }

            return {
                organization: ingComp.organization["@id"],
            };
        });
    };

    static ingredientFormToIngredientWrite = (ingredientForm: IngredientFormType, managedBy: IRI): IngredientWrite => {
        const newIngSupliers = IngredientService.makeIngredientSuppliers(
            ingredientForm.suppliers,
            resolveNewSupplierManager(ingredientForm, managedBy),
        );
        const newIngCompanies = IngredientService.makeShares(ingredientForm.shares);

        const ingredientToCreate: IngredientWrite = {
            id: ingredientForm.id,

            name: formatStringForBackend(ingredientForm.name),
            clientId: formatStringForBackend(ingredientForm.clientId),
            ingredientReferenceId: ingredientForm.ingredientReferenceId,
            referenceSource: ingredientForm.referenceSource,
            ingredientLine: ingredientForm.ingredientLine?.["@id"] ?? null,
            organic: ingredientForm.organic,
            ownedBy: ingredientForm.ownedBy?.["@id"] ?? undefined,
            suppliers: newIngSupliers,
            shares: newIngCompanies,
            jsonAttributes: ingredientForm.jsonAttributes ?? undefined,
            tags: ingredientForm.tags ?? undefined,
        };

        return ingredientToCreate;
    };
}
